/**
 * 66.7, the cover system (decision 144; ARCHITECTURE §5.15) — lane F's half, step F1: **where cover is** beside the props,
 * and **whether a spot is sheltered** from a threat. It produces lane C's shared contract, `CoverPoint` (src/types/world.ts),
 * which the bots (`coverMoves`), the take-cover verb and supervisor-1's controls read.
 *
 * - **Cover points** (`coverPointsNear`): beside each prop a body cannot walk through (a rock, a trunk, a structure's
 *   parts, a ruin, a felled trunk's discs — the walk's own footprints), points round it just past the walk's clearance
 *   (68.16's rule: a point inside that ring is never reached), none inside any prop's clearance (66.7 (b)). Each carries
 *   the way it faces (toward its prop), its shelter by direction (the tallest stance its props hide from a standing eye
 *   on each of COVER_POINT_BEARINGS bearings round it, level with its ground), and its peeks: over (a standing eye at the
 *   point sees past) and round its left and right edges (a side step whose eye, at the stance hidden, sees past). The
 *   lines are tested exactly against the shots' own hit shapes (core/hits.ts), followed a person past the prop — the
 *   cover beside the point, not the field beyond (that is `shelter`'s).
 * - **Shelter** (`shelter`): a spot against a threat's eye, at a stance — covered (by the ground, the fine ground a
 *   person's line reads, or a prop: a shot would stop), concealed (smoke, or a crown deep enough: a shot would pass), or
 *   open. Exact: the same lines the bots' sight and the bullets read. F2's tables answer it where they can.
 *
 * **Cached, deterministic.** A prop's points are a pure function of the world round it — its footprint, the footprints
 * whose clearance its points could fall in, the shapes its lines could meet (core/hits.ts keeps a shape's object while its
 * prop and the ground under it are unchanged: a crater or a changed form makes new ones), and the config. They are kept
 * per prop and re-derived only where those changed, so an answer never depends on who asked first (64.8; watched =
 * auto-resolved). Not state: nothing here enters the match, its checksum or its saves.
 */
import type { CoverHides, CoverPoint, CoverPointKind, GameConfig, GameState, PlanetState, Stance, Vec3 } from '../types';
import { COVER_POINT_BEARINGS, FOLIAGE_SIGHT } from '../constants';
import { vecAdd, vecCross, vecDot, vecLength, vecNormalize, vecScale, vecSub, surfaceTangentFrame } from '../math';
import { dacos, datan2, dcos, dsin } from './dmath';
import { obstaclesNear, projectToTangent, type PlanetObstacles, type WalkObstacle } from './surfaceWalk';
import { footprintObstacles, personMover, planetShapes, firstHit, shapesWithin, insideBuilding, type HitShape, type PlanetShapes } from './hits';
import { segmentCovered } from './bullets';
import { personLineOfSight, personPointOn, personSurfaceRadius, personGroundHit } from './fineTerrain';
import { checkCollision } from './physics';
import { groundMaterialAt } from './groundMaterial';
import { roundMaterial, wearOn } from './groundWear';
import { GROUND_BULLETS } from '../constants/ground';
import { personSetOf, PERSON_BODY_HEIGHT, type PersonSet } from './scaleProfile';
import { stanceCentreHeight, stanceEyeScale, stanceSpheres } from './stance';
import { smokeBlocks } from './smoke';
import { clearOfCollapse } from './collapseHazard';
import { crownCrossed, crownToHide } from './foliage';
import { gridLine, reliefReader, reliefReaderXYZ, reliefCellsNear } from './reliefGrid';
import { weaponSpec } from './weapons';

/** The dials (person units; × the scale's person). */
export const COVER_POINTS = {
  /** A point stands just past the walk's clearance round its prop (68.16): the prop, a body and a half, and this × k. */
  clearanceBodies: 1.5,
  marginK: 0.1,
  /** No point inside any prop's walk clearance (66.7 (b)): the prop, a body and a half, and this × k. */
  keepOutK: 0.05,
  /** Points round each prop. */
  around: 8,
  /** The threat a point's shelter is taken against: a standing eye this many people away, level with the point's ground. */
  threatPeople: 12,
  /** How far past its prop a point's line is followed (people). */
  pastPeople: 1,
  /** The side steps a peek tries (person widths), nearest first. */
  sideSteps: [0.5, 1] as readonly number[],
  /** F2: the relief grid answers the ground's part of a line when it clears or meets the grid's ground by this much (people), else the exact ray (core/reliefGrid.ts RELIEF). */
  gridMarginPeople: 0.1,
  /** F2: lines longer than this (people) go to the exact ray (and its horizon cull, 62.59), not the grid. */
  gridReachPeople: 150,
} as const;

/**
 * 70.3 (GameConfig.coverAcross; sweeps/2026-10-28-70.3-tree-cover.md): cover read across the body. A prop hides a stance
 * where the lines to its hit sphere's centre and to either edge — `edgeShare` of the sphere's radius each way, level and
 * square to the line — all meet it; the centre's line alone let a thin trunk (a birch 0.47 people across, a fir 0.6) hide a
 * standing body whose sphere is 1.1 people across, while its wood stopped under half of it. `kneelAtTrunks`: a holder at a
 * trunk that hides no standing body kneels to lean round it (the `edge` pose), not stands (`corner`) — the stance a thin
 * trunk hides worst. The ground's lines are as they were (a lip or a ridge is wide).
 */
export const COVER_ACROSS = { edgeShare: 0.6, kneelAtTrunks: true };

/** 70.3: cover read across the body — on when set true, or on the action clock unless set false (the classic game's only when set). */
export function coverAcrossOn(config: Partial<Pick<GameConfig, 'coverAcross' | 'clock'>> | undefined): boolean {
  return config?.coverAcross === true || (config?.clock === 'action' && config.coverAcross !== false);
}

/** 70.3: a body's two edges as seen along a line toward it (`along`, any length): its middle moved `half` either way, level at `up` and square to the line. Null when the line runs along `up`. */
export function bodyEdges(body: Vec3, along: Vec3, up: Vec3, half: number): [Vec3, Vec3] | null {
  const across = vecCross(along, up), L = vecLength(across);
  if (!(L > 0)) return null;
  const s = vecScale(across, half / L);
  return [vecAdd(body, s), vecSub(body, s)];
}

/**
 * 70.3: a body (its hit sphere's centre `body`, radius `r`, standing on ground whose up is `up`) covered from `eye` by the
 * props — the line to its centre (segmentCovered) and, with coverAcross on, the lines to either edge (COVER_ACROSS) too.
 */
export function propsCoverBody(state: Pick<GameState, 'planets' | 'rocks' | 'flora' | 'structures'> & Partial<Pick<GameState, 'config'>>, eye: Vec3, body: Vec3, up: Vec3, r: number, cache?: Map<number, WalkObstacle[]>, step?: number): boolean {
  if (!segmentCovered(eye, body, state, cache, step)) return false;
  if (!coverAcrossOn(state.config)) return true;
  const e = bodyEdges(body, vecSub(body, eye), up, COVER_ACROSS.edgeShare * r);
  return !e || (segmentCovered(eye, e[0], state, cache, step) && segmentCovered(eye, e[1], state, cache, step));
}

/** The radius of a stance's middle hit sphere (the one its centre height is: stanceCentreHeight). */
export function stanceSphereRadius(stance: Stance | undefined, ps: Pick<PersonSet, 'hitboxCenter' | 'hitboxRadius' | 'k'>): number {
  const s = stanceSpheres(stance, ps);
  return s[s.length >> 1].r;
}

/**
 * 63.5's last reader (cover by the ground): the posts a crater makes — on `around` rays from its middle, the spot nearest
 * its lip (from a person inside it in to `fromShare` of its radius, a person at a time) where a standing eye fires over the
 * lip and the lip hides a crouched body straight out (a fighting post in the bowl's edge); none on a ray with no such spot, none for a crater under
 * `minPeople` across. What a lip hides is read from the ground as dug, so each material's crater gives its own cover:
 * rock's shallow rimmed bowl, soil's deeper one, sand's wide slump.
 */
export const CRATER_COVER = { around: 8, fromShare: 0.5, minPeople: 1.5 } as const;

/** F2: the terrain spots' dials (people). */
export const TERRAIN_SPOTS = {
  /** Spots every this many relief cells (each about a person) on each face's lattice. */
  stride: 2,
  /** A crest: the ground this much lower, on average, 1–3 people toward the threat. */
  crestFall: 0.3,
  /** A reverse slope: the ground that hides it rises within this many people toward the threat. */
  reverseReach: 5,
  /** A spot's rise: its ground over the mean of the ground this far round it. */
  riseReach: 3,
  /** A spot's own line is read over its last this-many people; beyond, the rays fanned from the threat (shared by every spot). */
  nearPeople: 12,
  /**
   * 69.67: the rays read the ground out this many of the threat's reach from it — a person's rifle round's flight at the
   * scale (its speed × its life: ~545 people at `human`). Past it the threat cannot hit a person, so the ground there is
   * not walked a person a step (one threat 1,863 people off was 31,000 samples, most of a 58–87 ms tick); a spot still
   * reads its own last `nearPeople`.
   */
  rayReach: 1,
} as const;

const TALLEST_FIRST: readonly Stance[] = ['stand', 'crouch', 'prone'];

export type ShelterKind = 'open' | 'concealed' | 'covered';
/** `shelter`'s answer: how a spot stands against a threat, and what gives it. */
export interface Shelter { kind: ShelterKind; by: 'ground' | 'prop' | 'smoke' | 'foliage' | null }

/** Which of a cover point's bearings (its `shelter` index) a world point `toward` lies on: 0 its facing, turning to the right of a body facing the cover. */
export function coverBearing(point: Pick<CoverPoint, 'dir' | 'facing'>, toward: Vec3): number {
  const up = vecNormalize(point.dir), t = projectToTangent(toward, up);
  if (vecLength(t) < 1e-12) return 0;
  const right = vecCross(point.facing, up);
  let a = datan2(vecDot(t, right), vecDot(t, point.facing));
  if (a < 0) a += 2 * Math.PI;
  return Math.round((a / (2 * Math.PI)) * COVER_POINT_BEARINGS) % COVER_POINT_BEARINGS;
}

// ---------------------------------------------------------------------------
// Shelter: a spot against a threat's eye, exactly
// ---------------------------------------------------------------------------

/**
 * Is a body at `dir` (planet `planetIndex`), at `stance`, sheltered from an eye at `threatEye` (world)? Covered by the
 * ground (the person's line: the mesh and the fine ground) or by a prop (the shots' shapes) — a shot stops; concealed by
 * smoke or by enough crown (core/foliage.ts crownToHide: a person's worth for a standing body, less lower down; never at arm's
 * length) — a shot passes; else open. The body's point is its stance's centre (the bots' aim point).
 */
export function shelter(state: Pick<GameState, 'planets' | 'config' | 'rocks' | 'flora' | 'structures'> & Partial<Pick<GameState, 'smokes'>>, planetIndex: number, dir: Vec3, threatEye: Vec3, stance: Stance = 'stand'): Shelter {
  const planet = state.planets[planetIndex];
  if (!planet) return { kind: 'open', by: null };
  const ps = personSetOf(state), up = vecNormalize(dir);
  const body = personPointOn(planet, up, ps, stanceCentreHeight(stance, ps));
  // 63.5 cover by material: ground that hides the body but lets a round through (a thin sand or snow lip) conceals it
  if (groundBlocks(state, planetIndex, threatEye, body)) return { kind: groundLetsThrough(state, planetIndex, threatEye, body) ? 'concealed' : 'covered', by: 'ground' };
  if (propsCoverBody(state, threatEye, body, up, stanceSphereRadius(stance, ps))) return { kind: 'covered', by: 'prop' };   // (70.3: across the body)
  if (smokeBlocks(state.smokes, threatEye, body)) return { kind: 'concealed', by: 'smoke' };
  // (the crown rule the bots' sight reads with foliageSight on — core/foliage.ts crownToHide — whether or not it is on:
  // concealment is what the place gives)
  const person = PERSON_BODY_HEIGHT * ps.k;
  if (vecLength(vecSub(body, threatEye)) > FOLIAGE_SIGHT.armPeople * person && crownCrossed(threatEye, body, planetIndex, state) >= crownToHide(stance, person)) return { kind: 'concealed', by: 'foliage' };
  return { kind: 'open', by: null };
}

/**
 * 63.5's last reader, cover by material: does the ground hiding `body` from `eye` let a round through? From the body
 * toward the eye, the first span of ground the line crosses (within the furthest any ground passes a round, and a person)
 * is judged by its material where the line enters it — GROUND_BULLETS: rock, soil, ice and salt stop a round; sand, ash,
 * mud and snow pass one through less than their `through` people; soil worn past GROUND_WEAR.hitsToPass rounds passes as
 * sand (core/groundWear.ts) — thinner than its `through`: it lets one through. A far ridge, or ground that stops a round,
 * is cover. (Rain's mud is the bullets' to read at the moment; the cover map reads the dry ground.) Nothing to march where
 * the ground under the body stops rounds and the planet has no wear.
 */
export function groundLetsThrough(state: Pick<GameState, 'planets' | 'config'> & Partial<Pick<GameState, 'groundWear'>>, planetIndex: number, eye: Vec3, body: Vec3): boolean {
  const planet = state.planets[planetIndex];
  if (!planet) return false;
  const wearHere = wearOn(state, planetIndex);
  if (!wearHere && !(GROUND_BULLETS[groundMaterialAt(planet, vecNormalize(vecSub(body, planet.position)))].through > 0)) return false;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, step = 0.1 * person;
  const d = vecNormalize(vecSub(eye, body)), reach = (MAX_THROUGH + 1) * person;
  const under = (q: Vec3) => (personGroundHit(q, state) ?? checkCollision(q, state.planets)) !== null;
  let entry = -1, through = 0;
  for (let x = step; x <= reach + 1e-12; x += step) {
    const q = vecAdd(body, vecScale(d, x)), u = under(q);
    if (entry < 0) {
      if (!u) continue;
      entry = x;
      through = GROUND_BULLETS[roundMaterial(state, planetIndex, q)].through;
      if (!(through > 0)) return false;
    } else if (!u) return x - entry <= through * person + 1e-12;
    else if (x - entry > through * person + 1e-12) return false;
  }
  return false;
}
/** The most ground any material passes a round through (people). */
const MAX_THROUGH = Math.max(...Object.values(GROUND_BULLETS).map(g => g.through));

/**
 * Does the ground (the mesh and the fine ground, as a person's line reads it) stand between `a` and `b`? F2: the relief
 * grid answers when it is sure (the line clears or meets the grid's ground by COVER_POINTS.gridMarginPeople — never wrong
 * against the exact line in 8,000 random lines round a battle); otherwise, and for a long line, the exact person's line
 * (without smoke: smoke conceals, it is not cover).
 */
export function groundBlocks(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, a: Vec3, b: Vec3): boolean {
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  if (vecLength(vecSub(b, a)) <= COVER_POINTS.gridReachPeople * person) {
    const g = gridLine(state, planetIndex, a, b, COVER_POINTS.gridMarginPeople * person);
    if (g !== 'unsure') return g === 'blocked';
  }
  return !personLineOfSight(a, b, { planets: state.planets, config: state.config });
}

// ---------------------------------------------------------------------------
// Terrain spots: crests, reverse slopes, dead ground — against a threat
// ---------------------------------------------------------------------------

export type TerrainSpotKind = 'crest' | 'reverseSlope' | 'deadGround';
/** F2: a spot the ground makes against a threat (§5.15's height: a crest sees; a reverse slope hides a step behind one; dead ground hides). */
export interface TerrainSpot {
  /** Where the body stands (a unit surface direction: a relief cell's centre, so its place never moves). */
  dir: Vec3;
  kind: TerrainSpotKind;
  /** The tallest stance the ground hides from the threat's eye here (null: none). */
  hides: Stance | null;
  /** The lowest stance whose eye sees the threat's eye from here (null: none). */
  sees: Stance | null;
  /** The ground here over the mean of the ground round it (people): + a rise, − a hollow. */
  rise: number;
  /** A reverse slope's crest: the step up, toward the threat, from which a body sees it. */
  crest?: Vec3;
}

/**
 * F2 (§5.15's `terrainSpotsNear`): the crests, reverse slopes and dead ground within `radius` (world units) of `dir` on
 * planet `planetIndex`, against a threat's eye `threatEye` (world). Spots stand on the relief grid's lattice (every
 * TERRAIN_SPOTS.stride cells), so a spot's place does not move as the asker does.
 *
 * Each is judged as a viewshed from the threat's eye over the grid: the ground's highest elevation seen from the eye on
 * the way to a spot — far off, along rays fanned from the threat half a person apart where the spots are (shared by every
 * spot on a ray), and over the spot's last TERRAIN_SPOTS.nearPeople along its own line. A body point is hidden when that
 * ground stands over its line by more than COVER_POINTS.gridMarginPeople, and seen when its line clears the ground by as
 * much; in between it is neither claimed. The props' cover is `coverPointsNear`'s; a spot here may stand by a prop too.
 */
export function terrainSpotsNear(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, dir: Vec3, radius: number, threatEye: Vec3): TerrainSpot[] {
  const planet = state.planets[planetIndex];
  if (!planet) return [];
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, R = planet.radius, C = planet.position, T = threatEye;
  const m = COVER_POINTS.gridMarginPeople * person;
  const rifle = weaponSpec(ps), reachK = Math.floor((TERRAIN_SPOTS.rayReach * rifle.speed * rifle.life) / person);   // (the rays' last sample: the threat's reach)
  const centre = vecNormalize(dir);
  const cells = reliefCellsNear(state, planetIndex, centre, radius / R, TERRAIN_SPOTS.stride);
  if (cells.length === 0) return [];
  const upT = vecNormalize(vecSub(T, C));
  let f = projectToTangent(centre, upT);
  if (vecLength(f) < 1e-12) f = surfaceTangentFrame(upT).tx;
  f = vecNormalize(f);
  const g = vecCross(upT, f);
  const groundAt = reliefReader(state, planetIndex);
  const onGround = (d: Vec3, h = 0) => vecAdd(C, vecScale(d, groundAt(d) + h));
  // a step of a whole number of people along tangent t: its turn's cosine and sine kept per query (the same few for every spot)
  const turns = new Map<number, [number, number]>();
  const step = (d: Vec3, t: Vec3, people: number) => {
    let cs = turns.get(people);
    if (!cs) { const th = (people * person) / R; cs = [dcos(th), dsin(th)]; turns.set(people, cs); }
    return vecNormalize(vecAdd(vecScale(d, cs[0]), vecScale(t, cs[1])));
  };
  const groundXYZ = reliefReaderXYZ(state, planetIndex);
  // the ground's elevation from the eye at ground point (direction px, py, pz; unit): a sine (v·up / |v|), less (lo) and plus
  // (hi) the margin over its distance — scalars, for the hot loops
  let lo1 = 0, hi1 = 0;
  const lowHighAt = (px: number, py: number, pz: number) => {
    const gr = groundXYZ(px, py, pz);
    const vx = C.x + px * gr - T.x, vy = C.y + py * gr - T.y, vz = C.z + pz * gr - T.z, l = Math.sqrt(vx * vx + vy * vy + vz * vz);
    const sn = (vx * upT.x + vy * upT.y + vz * upT.z) / l;
    lo1 = sn - m / l; hi1 = sn + m / l;
  };
  // the rays fanned from the threat (a person apart where the spots are furthest): per ray, the running highest elevation
  // (low and high) at each person along the ground; the turn from the threat's foot by x people, once a query
  const far = farthest(cells);
  const dAlpha = person / Math.max(person, far);
  const cosX: number[] = [1], sinX: number[] = [0];
  const rays = new Map<number, { ux: number; uy: number; uz: number; lo: number[]; hi: number[] }>();
  const ray = (b: number, k: number) => {
    let r = rays.get(b);
    if (!r) { const ca = dcos(b * dAlpha), sa = dsin(b * dAlpha); r = { ux: f.x * ca + g.x * sa, uy: f.y * ca + g.y * sa, uz: f.z * ca + g.z * sa, lo: [-Infinity], hi: [-Infinity] }; rays.set(b, r); }
    for (let x = cosX.length; x <= k; x++) { cosX.push(dcos((x * person) / R)); sinX.push(dsin((x * person) / R)); }
    for (let x = r.lo.length; x <= k; x++) {
      lowHighAt(upT.x * cosX[x] + r.ux * sinX[x], upT.y * cosX[x] + r.uy * sinX[x], upT.z * cosX[x] + r.uz * sinX[x]);
      r.lo.push(Math.max(r.lo[x - 1], lo1)); r.hi.push(Math.max(r.hi[x - 1], hi1));
    }
    return r;
  };
  function farthest(ds: readonly Vec3[]): number { let mx = 0; for (const d of ds) mx = Math.max(mx, dacos(Math.max(-1, Math.min(1, vecDot(d, upT)))) * R); return mx + TERRAIN_SPOTS.reverseReach * person; }
  /** The ground's highest elevation (low, high) from the eye on the way to the point over `d`: the shared ray, then its own line's last stretch. */
  const horizonTo = (d: Vec3, P: Vec3): [number, number] => {
    const t = projectToTangent(vecSub(P, T), upT);
    const alpha = datan2(vecDot(t, g), vecDot(t, f));
    const D = dacos(Math.max(-1, Math.min(1, vecDot(d, upT)))) * R / person;   // people along the ground
    let lo = -Infinity, hi = -Infinity;
    const kFar = Math.min(reachK, Math.floor(D - TERRAIN_SPOTS.nearPeople));
    if (kFar >= 1) { const r = ray(Math.round(alpha / dAlpha), kFar); lo = r.lo[kFar]; hi = r.hi[kFar]; }
    // the spot's own line, from half a person off it back toward the eye
    const vx = T.x - P.x, vy = T.y - P.y, vz = T.z - P.z, L = Math.sqrt(vx * vx + vy * vy + vz * vz);
    for (let k = 0; k < TERRAIN_SPOTS.nearPeople; k++) {
      const s2 = ((k + 0.5) * person) / L;
      if (s2 >= 1) break;
      const qx = P.x + vx * s2 - C.x, qy = P.y + vy * s2 - C.y, qz = P.z + vz * s2 - C.z, ql = Math.sqrt(qx * qx + qy * qy + qz * qz);
      lowHighAt(qx / ql, qy / ql, qz / ql);
      if (lo1 > lo) lo = lo1; if (hi1 > hi) hi = hi1;
    }
    return [lo, hi];
  };
  const elevation = (P: Vec3) => { const v = vecSub(P, T); return vecDot(v, upT) / vecLength(v); };
  const judge = (d: Vec3): { hides: Stance | null; sees: Stance | null } => {
    const [lo, hi] = horizonTo(d, onGround(d, ps.losEyeHeight));
    let hides: Stance | null = null, sees: Stance | null = null;
    for (const s of TALLEST_FIRST) if (lo > elevation(onGround(d, stanceCentreHeight(s, ps)))) { hides = s; break; }
    for (const s of ['prone', 'crouch', 'stand'] as const) if (hi < elevation(onGround(d, ps.losEyeHeight * stanceEyeScale(s)))) { sees = s; break; }
    return { hides, sees };
  };
  const out: TerrainSpot[] = [];
  for (const d of cells) {
    const toward = projectToTangent(vecSub(T, C), d);
    if (vecLength(toward) < 1e-12) continue;
    const t = vecNormalize(toward), side = vecCross(d, t), back = vecScale(t, -1);
    const g0 = groundAt(d);
    const around = (groundAt(step(d, t, TERRAIN_SPOTS.riseReach)) + groundAt(step(d, back, TERRAIN_SPOTS.riseReach)) + groundAt(step(d, side, TERRAIN_SPOTS.riseReach)) + groundAt(step(d, vecScale(side, -1), TERRAIN_SPOTS.riseReach))) / 4;
    const rise = (g0 - around) / person;
    const { hides, sees } = judge(d);
    if (hides) {
      // hidden by the ground: a reverse slope when the ground that hides it rises within reach toward the threat and a body
      // standing there sees it (the step up to fire from); else dead ground
      let crest: Vec3 | null = null, top = -Infinity;
      for (let k = 1; k <= TERRAIN_SPOTS.reverseReach; k++) { const dk = step(d, t, k), gk = groundAt(dk); if (gk > top) { top = gk; crest = dk; } }
      if (crest && top > g0 && judge(crest).sees !== null) out.push({ dir: d, kind: 'reverseSlope', hides, sees, rise, crest });
      else out.push({ dir: d, kind: 'deadGround', hides, sees, rise });
      continue;
    }
    // seen: a crest when the ground falls away toward the threat and does not climb behind it
    if (!sees) continue;
    const ahead = (groundAt(step(d, t, 1)) + groundAt(step(d, t, 2)) + groundAt(step(d, t, 3))) / 3;
    const behind = (groundAt(step(d, back, 1)) + groundAt(step(d, back, 2)) + groundAt(step(d, back, 3))) / 3;
    if ((g0 - ahead) / person >= TERRAIN_SPOTS.crestFall && (g0 - behind) / person >= -0.1) out.push({ dir: d, kind: 'crest', hides: null, sees, rise });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Cover points: beside each prop, kept per prop
// ---------------------------------------------------------------------------

interface Kept { radius: number; height: number; foots: readonly number[]; shapes: readonly HitShape[]; out: readonly CoverPoint[] }
/**
 * Per config (a match's), per planet and prop footprint (by what it is and where: a crater makes new footprint objects even
 * far off, so the key is its ref and direction, its size checked): its points, with the world they were derived from —
 * the footprints whose clearance its points could fall in (their directions and sizes) and the shapes its lines could meet.
 */
let kept = new WeakMap<object, Map<string, Kept>>();
/** The fast path: per footprint object, its points while the planet's shapes, its footprint list and the config are the very objects they were checked against. */
let checked = new WeakMap<WalkObstacle, { shapes: object; foot: object; config: unknown; out: readonly CoverPoint[] }>();
/** For the tests: run `fn` with nothing kept, then put back what was. */
export function coverFromNothing<T>(fn: () => T): T {
  const craters = craterKept; craterKept = new WeakMap();
  try { return coverFromNothingProps(fn); } finally { craterKept = craters; }
}
function coverFromNothingProps<T>(fn: () => T): T {
  const saved = { kept, checked };
  kept = new WeakMap(); checked = new WeakMap();
  try { return fn(); } finally { ({ kept, checked } = saved); }
}
/** Per footprint list: each footprint's ordinal among those of its prop (a felled trunk lies as several discs) — its points' key. */
const ordinals = new WeakMap<PlanetObstacles, Map<WalkObstacle, number>>();
function ordinalOf(foot: PlanetObstacles, o: WalkObstacle): number {
  let m = ordinals.get(foot);
  if (!m) {
    m = new Map();
    const seen = new Map<string, number>();
    for (const q of foot.list) { const r = `${q.ref?.kind}:${q.ref?.id}`; const i = seen.get(r) ?? 0; seen.set(r, i + 1); m.set(q, i); }
    ordinals.set(foot, m);
  }
  return m.get(o) ?? 0;
}

const sameList = <T>(a: readonly T[], b: readonly T[]) => a.length === b.length && a.every((x, i) => x === b[i]);
const footValues = (qs: readonly WalkObstacle[]) => qs.flatMap(q => [q.dir.x, q.dir.y, q.dir.z, q.radius]);
const arcOn = (a: Vec3, b: Vec3, R: number) => dacos(Math.max(-1, Math.min(1, vecDot(a, b)))) * R;

/** What gives a prop's cover, by the contract's kinds. */
function kindOf(state: Pick<GameState, 'structures'>, o: WalkObstacle): CoverPointKind {
  switch (o.ref?.kind) {
    case 'rock': return 'rock';
    case 'flora': return 'trunk';
    case 'ruin': return 'ruin';
    case 'debris': return 'wreck';
    case 'structure': {
      const t = state.structures.find(s => s.id === o.ref!.id)?.type;
      return t === 'hmg' ? 'sandbags' : t === 'supply' ? 'crate' : 'wall';
    }
    default: return 'rock';
  }
}

/**
 * The cover points within `radius` (world units, along the ground) of `dir` on planet `planetIndex`: beside every prop
 * whose points could fall there, in the props' order then round each prop. Each point stands outside every prop's walk
 * clearance and hides at least a prone body straight across its prop.
 */
export function coverPointsNear(state: Pick<GameState, 'planets' | 'config' | 'rocks' | 'flora' | 'structures'> & Partial<Pick<GameState, 'ruins' | 'terrainEdits' | 'groundWear'>>, planetIndex: number, dir: Vec3, radius: number): CoverPoint[] {
  const planet = state.planets[planetIndex];
  if (!planet) return [];
  const ps = personSetOf(state);
  const foot = footprintObstacles(state, planetIndex, personMover(ps));
  // 63.5's last reader: the craters' posts, after the props' (in the log's order)
  // (72.3 step 7: no post in a building coming down)
  const craters = (out: CoverPoint[]) => { for (const p of craterPointsNear(state, planetIndex, planet, ps, foot, dir, radius)) out.push(p); return clearOfCollapse(state, planetIndex, out); };
  if (foot.list.length === 0) return craters([]);
  const R = planet.radius, n = vecNormalize(dir);
  const ring = (o: WalkObstacle) => o.radius + COVER_POINTS.clearanceBodies * ps.unitRadius + COVER_POINTS.marginK * ps.k;
  const out: CoverPoint[] = [];
  for (const o of obstaclesNear(foot, n, (radius + foot.maxRadius + 2 * ps.unitRadius + ps.k) / R)) {
    if (arcOn(o.dir, n, R) > radius + ring(o)) continue;
    for (const p of pointsOf(state, planetIndex, planet, ps, foot, o)) if (arcOn(p.dir, n, R) <= radius) out.push(p);
  }
  return craters(out);
}

/** 63.5 cover by material: each crater post's lip line (its crouched body, a standing eye out over the lip) — its concealment judged again under wear. */
const lipOf = new WeakMap<CoverPoint, { body: Vec3; eye: Vec3 }>();

/**
 * Per config, per planet: each crater's posts by its index in the log, with the log's length when they were read — still
 * good while no later crater overlaps it (the only edits that change its ground), so a new crater re-reads only its
 * neighbours — and while the footprints near it (whose clearance its posts keep out of) are the same, by value. `last` is
 * the log's last entry then: the log only grows by appending, so a different object there is another history (a branch a
 * planner or a test took from an earlier state), which reads afresh.
 */
let craterKept = new WeakMap<object, Map<number, Map<number, { at: number; last: object; foots: readonly number[]; out: readonly CoverPoint[] }>>>();

/**
 * 63.5's last reader (cover by the ground, with 66.7's contract): the posts the craters within `radius` of `dir` make —
 * each crater's (CRATER_COVER) in the bowl just inside its lip, facing out over it, kind `'crater'`, no prop. What a lip
 * hides on each bearing is read from the ground as dug (`groundBlocks`, the line a person's sight and a shot read), to the
 * lip and a person past it — the cover beside the post, not the field beyond — so the material that shaped the crater
 * (rock's rim, sand's slump) shapes its cover. A post that hides nobody straight out over its lip is none; none stands in
 * a prop's clearance or a building. Kept while the planet's ground and the config are the very objects they were.
 */
function craterPointsNear(state: Pick<GameState, 'planets' | 'config' | 'rocks' | 'flora' | 'structures'> & Partial<Pick<GameState, 'ruins' | 'terrainEdits' | 'groundWear'>>, planetIndex: number, planet: PlanetState, ps: PersonSet, foot: PlanetObstacles, dir: Vec3, radius: number): CoverPoint[] {
  const edits = state.terrainEdits;
  if (!edits?.length) return [];
  const person = PERSON_BODY_HEIGHT * ps.k, R = planet.radius, n = vecNormalize(dir);
  let byPlanet = craterKept.get(state.config);
  if (!byPlanet) { byPlanet = new Map(); craterKept.set(state.config, byPlanet); }
  let memo = byPlanet.get(planetIndex);
  if (!memo) { memo = new Map(); byPlanet.set(planetIndex, memo); }
  const midOf = (e: (typeof edits)[number]) => vecNormalize(vecSub(e.point, planet.position));
  /** Has a crater after `from` in the log changed the ground of crater `e` (overlapping it)? */
  const touched = (e: (typeof edits)[number], i: number, from: number) => {
    for (let k = Math.max(from, i + 1); k < edits.length; k++) {
      const o = edits[k];
      if (o.planetIndex === planetIndex && arcOn(midOf(o), midOf(e), R) < o.radius + e.radius + person) return true;
    }
    return false;
  };
  const out: CoverPoint[] = [];
  const wearHere = wearOn(state, planetIndex);
  edits.forEach((e, i) => {
    if (e.kind !== 'crater' || e.planetIndex !== planetIndex || e.radius < CRATER_COVER.minPeople * person) return;
    const mid = midOf(e);
    if (arcOn(mid, n, R) > radius + e.radius) return;
    const foots = footValues(obstaclesNear(foot, mid, (e.radius + foot.maxRadius + 2 * ps.unitRadius + ps.k) / R));
    let k = memo!.get(i);
    if (!k || k.at > edits.length || edits[k.at - 1] !== k.last || touched(e, i, k.at) || !sameList(k.foots, foots)) {
      k = { at: edits.length, last: edits[edits.length - 1], foots, out: craterPosts(state, planetIndex, planet, ps, foot, mid, e.radius, i) };
      memo!.set(i, k);
    } else if (k.at !== edits.length) { k.at = edits.length; k.last = edits[edits.length - 1]; }
    for (const p of k.out) {
      if (arcOn(p.dir, n, R) > radius) continue;
      // 63.5 cover by material: under fire a soil lip wears through (GROUND_WEAR) — judged again from its lip line, the cache kept
      const lip = wearHere ? lipOf.get(p) : undefined;
      const thin = lip ? groundLetsThrough(state, planetIndex, lip.eye, lip.body) : !!p.concealment;
      if (thin === !!p.concealment) { out.push(p); continue; }
      const { concealment: _c, ...rest } = p;
      out.push(thin ? { ...rest, concealment: true } : rest);
    }
  });
  return out;
}

/** One crater's posts (see craterPointsNear). */
function craterPosts(state: Pick<GameState, 'planets' | 'config' | 'rocks' | 'flora' | 'structures'> & Partial<Pick<GameState, 'ruins'>>, planetIndex: number, planet: PlanetState, ps: PersonSet, foot: PlanetObstacles, mid: Vec3, r: number, index: number): readonly CoverPoint[] {
  const person = PERSON_BODY_HEIGHT * ps.k, R = planet.radius;
  const keepOut = (q: WalkObstacle) => q.radius + COVER_POINTS.clearanceBodies * ps.unitRadius + COVER_POINTS.keepOutK * ps.k;
  const D = COVER_POINTS.threatPeople * person;
  /** Is the line from `h` up at `at` toward a standing eye D along bearing `u`, level with this ground, met by the ground within `reach` (to the lip and a person past it)? */
  const meets = (at: Vec3, ground: number, h: number, u: Vec3, reach: number) => {
    const a = vecAdd(planet.position, vecScale(at, ground + h));
    const d = vecAdd(vecScale(u, D), vecScale(at, ps.losEyeHeight - h)), len = vecLength(d);
    return groundBlocks(state, planetIndex, a, vecAdd(a, vecScale(d, Math.min(1, reach / len))));
  };
  const near = obstaclesNear(foot, mid, (r + foot.maxRadius + 2 * ps.unitRadius + ps.k) / R);
  const { tx, ty } = surfaceTangentFrame(mid);
  const crouch = stanceCentreHeight('crouch', ps);
  const out: CoverPoint[] = [];
  for (let j = 0; j < CRATER_COVER.around; j++) {
    const ang = (j / CRATER_COVER.around) * 2 * Math.PI;
    const t = vecAdd(vecScale(tx, dcos(ang)), vecScale(ty, dsin(ang)));
    // in from the lip a person at a time, to fromShare of the radius: the first spot (the nearest the lip) where a standing
    // eye fires over the lip and the lip still hides a crouch straight out, clear of every prop's clearance and building
    const at = (x: number) => vecNormalize(vecAdd(vecScale(mid, dcos(x / R)), vecScale(t, dsin(x / R))));
    const facingAt = (d: Vec3) => vecNormalize(projectToTangent(vecSub(d, mid), d));   // out over the lip
    const reachAt = (x: number) => r - x + COVER_POINTS.pastPeople * person;   // to its lip and a person past it
    const post = (c: number) => {
      const d = at(c), g = personSurfaceRadius(planet, d, ps), f = facingAt(d), L = reachAt(c);
      return meets(d, g, crouch, f, L) && !meets(d, g, ps.losEyeHeight, f, L) && !near.some(q => arcOn(d, q.dir, R) < keepOut(q)) && !insideBuilding(state, planetIndex, d);
    };
    let x = r - person;
    while (x >= CRATER_COVER.fromShare * r && !post(x)) x -= person;
    if (x < CRATER_COVER.fromShare * r) continue;   // none on this ray
    const d = at(x), facing = facingAt(d), ground = personSurfaceRadius(planet, d, ps), reach = reachAt(x);
    const right = vecCross(facing, d);
    const shelterBy: CoverHides[] = [];
    for (let b = 0; b < COVER_POINT_BEARINGS; b++) {
      const a2 = (b / COVER_POINT_BEARINGS) * 2 * Math.PI;
      const u = vecAdd(vecScale(facing, dcos(a2)), vecScale(right, dsin(a2)));
      let hid: CoverHides = null;
      for (const st of TALLEST_FIRST) if (meets(d, ground, stanceCentreHeight(st, ps), u, reach)) { hid = st; break; }
      shelterBy.push(hid);
    }
    // 63.5 cover by material: a lip that lets a round through (sand, ash, mud, snow, thin) hides the crouch but does not stop the round
    const body = vecAdd(planet.position, vecScale(d, ground + crouch));
    const eyeOut = vecAdd(body, vecAdd(vecScale(facing, D), vecScale(d, ps.losEyeHeight - crouch)));
    const thin = groundLetsThrough(state, planetIndex, eyeOut, body);
    const made: CoverPoint = { planetIndex, key: `crater:${planetIndex}:${index}:${j}`, dir: d, kind: 'crater', facing, shelter: shelterBy, peeks: [{ side: 'over', dir: d }], ...(thin ? { concealment: true } : {}) };
    lipOf.set(made, { body, eye: eyeOut });
    out.push(made);
  }
  return out;
}

/**
 * The lines cover is read by (the cover points here; 66.9's slots, core/coverSlots.ts): `meets` — is the line from `h` up
 * at `at` toward a standing eye COVER_POINTS.threatPeople along bearing `u` (a tangent at `at`), level with this ground —
 * in its tangent plane, so the table is the props' and not the planet's curve — met by one of the shapes `cand` within
 * its run `L`? `shelterAround` — what those shapes hide from each of COVER_POINT_BEARINGS bearings round `at`, the first
 * along `facing`, turning to `facing` × up: the tallest stance a line from it meets.
 */
export function coverLines(planet: PlanetState, shapes: PlanetShapes, cand: readonly number[], ps: PersonSet, across = false) {
  const person = PERSON_BODY_HEIGHT * ps.k;
  const blocked = (a: Vec3, b: Vec3) => firstHit(a, b, shapes, cand, planet) !== null;
  const D = COVER_POINTS.threatPeople * person;
  /** `half` (70.3, `across`): the body's half-width the line is read at — met only where the lines from either edge, parallel to it, are met too. */
  const meets = (at: Vec3, ground: number, h: number, u: Vec3, L: number, half = 0) => {
    const a = vecAdd(planet.position, vecScale(at, ground + h));
    const d = vecAdd(vecScale(u, D), vecScale(at, ps.losEyeHeight - h)), len = vecLength(d);
    const run = vecScale(d, Math.min(1, L / len));
    if (!blocked(a, vecAdd(a, run))) return false;
    const e = half > 0 ? bodyEdges(a, d, at, half) : null;
    return !e || (blocked(e[0], vecAdd(e[0], run)) && blocked(e[1], vecAdd(e[1], run)));
  };
  /** Each stance's half-width read (70.3): COVER_ACROSS.edgeShare of its middle sphere's radius with `across`, else none. */
  const halfOf = (s: Stance) => across ? COVER_ACROSS.edgeShare * stanceSphereRadius(s, ps) : 0;
  const shelterAround = (at: Vec3, facing: Vec3, ground: number, L: number): CoverHides[] => {
    const right = vecCross(facing, at);
    const shelterBy: CoverHides[] = [];
    for (let b = 0; b < COVER_POINT_BEARINGS; b++) {
      const a2 = (b / COVER_POINT_BEARINGS) * 2 * Math.PI;
      const u = vecAdd(vecScale(facing, dcos(a2)), vecScale(right, dsin(a2)));
      let hid: CoverHides = null;
      for (const s of TALLEST_FIRST) if (meets(at, ground, stanceCentreHeight(s, ps), u, L, halfOf(s))) { hid = s; break; }
      shelterBy.push(hid);
    }
    return shelterBy;
  };
  return { meets, shelterAround };
}

/** A prop's points, kept while its footprint, the footprints and shapes near it and the config are the same. */
function pointsOf(state: Pick<GameState, 'planets' | 'config' | 'rocks' | 'flora' | 'structures'>, planetIndex: number, planet: PlanetState, ps: PersonSet, foot: PlanetObstacles, o: WalkObstacle): readonly CoverPoint[] {
  const shapes = planetShapes(state, planetIndex);
  const c = checked.get(o);
  if (c && c.shapes === shapes && c.foot === foot && c.config === state.config) return c.out;
  const person = PERSON_BODY_HEIGHT * ps.k, R = planet.radius;
  const ring = o.radius + COVER_POINTS.clearanceBodies * ps.unitRadius + COVER_POINTS.marginK * ps.k;
  const sideMax = Math.max(...COVER_POINTS.sideSteps) * 2 * ps.unitRadius;
  // what a point's lines can meet: its prop's ring, the line's run past the prop, a side step
  const reach = ring + sideMax + (2 * o.radius + COVER_POINTS.pastPeople * person);
  const centre = vecAdd(planet.position, vecScale(o.dir, personSurfaceRadius(planet, o.dir, ps)));
  const cand = shapesWithin(centre, reach + person, planet, shapes);
  const near = cand.map(i => shapes.list[i]);
  // the footprints whose clearance a point could fall in
  const keepOut = (q: WalkObstacle) => q.radius + COVER_POINTS.clearanceBodies * ps.unitRadius + COVER_POINTS.keepOutK * ps.k;
  const foots = obstaclesNear(foot, o.dir, (ring + foot.maxRadius + 2 * ps.unitRadius + ps.k) / R).filter(q => arcOn(q.dir, o.dir, R) <= ring + keepOut(q));
  let byProp = kept.get(state.config);
  if (!byProp) { byProp = new Map(); kept.set(state.config, byProp); }
  const propKey = `${planetIndex}:${o.ref?.kind ?? '-'}:${o.ref?.id ?? -1}:${ordinalOf(foot, o)}`;
  const key = `${propKey}|${o.dir.x}|${o.dir.y}|${o.dir.z}`;
  const fv = footValues(foots);
  const k = byProp.get(key);
  if (k && k.radius === o.radius && k.height === o.height && sameList(k.foots, fv) && sameList(k.shapes, near)) {
    checked.set(o, { shapes, foot, config: state.config, out: k.out });
    return k.out;
  }

  const run = (from: Vec3) => arcOn(from, o.dir, R) + o.radius + COVER_POINTS.pastPeople * person;
  const { meets, shelterAround } = coverLines(planet, shapes, cand, ps, coverAcrossOn(state.config));
  const kind = kindOf(state, o);
  const prop = o.ref ? { kind: o.ref.kind, id: o.ref.id } : undefined;
  const out: CoverPoint[] = [];
  const { tx, ty } = surfaceTangentFrame(o.dir);
  const th = ring / R;
  for (let j = 0; j < COVER_POINTS.around; j++) {
    const ang = (j / COVER_POINTS.around) * 2 * Math.PI;
    const t = vecAdd(vecScale(tx, dcos(ang)), vecScale(ty, dsin(ang)));
    const at = vecNormalize(vecAdd(vecScale(o.dir, dcos(th)), vecScale(t, dsin(th))));
    if (foots.some(q => arcOn(at, q.dir, R) < keepOut(q))) continue;   // 66.7 (b): never inside a clearance
    if (insideBuilding(state, planetIndex, at)) continue;   // 63.9: never inside a building's walls (its outline's discs leave its floor open)
    const facing = vecNormalize(projectToTangent(vecSub(o.dir, at), at));
    const right = vecCross(facing, at);
    const ground = personSurfaceRadius(planet, at, ps), L = run(at);
    const shelterBy = shelterAround(at, facing, ground, L);
    const hides = shelterBy[0];
    if (!hides) continue;   // hides nobody straight across its prop: not a cover point
    // the peeks: round each edge (a step whose eye at the stance hidden sees past), and over (a standing eye here sees past)
    const peeks: CoverPoint['peeks'] = [];
    const edge = (side: 'left' | 'right') => {
      const sign = side === 'right' ? 1 : -1;
      for (const w of COVER_POINTS.sideSteps) {
        const a3 = (w * 2 * ps.unitRadius) / R;
        const s2 = vecNormalize(vecAdd(vecScale(at, dcos(a3)), vecScale(right, sign * dsin(a3))));
        if (!meets(s2, personSurfaceRadius(planet, s2, ps), ps.losEyeHeight * stanceEyeScale(hides), facing, run(s2))) { peeks.push({ side, dir: s2 }); return; }
      }
    };
    edge('left'); edge('right');
    if (!meets(at, ground, ps.losEyeHeight, facing, L)) peeks.push({ side: 'over', dir: at });
    out.push({ planetIndex, key: `${propKey}:${j}`, dir: at, kind, ...(prop ? { prop } : {}), facing, shelter: shelterBy, peeks });
  }
  byProp.set(key, { radius: o.radius, height: o.height, foots: fv, shapes: near, out });
  checked.set(o, { shapes, foot, config: state.config, out });
  return out;
}
